import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { Button } from './ui/button';
import { Input } from './ui/input';
import RefundGiftSheetTerms from './RefundGiftSheetTerms';
import { Checkbox } from './ui/checkbox';
import { useNavigate } from '@tanstack/react-router';
import {
  claimTango as claimTango,
  TGetGiftCardsRes,
} from '@/services/giftCards';
import { useMutation } from '@tanstack/react-query';

import { TGiftOrDonation } from '@/lib/types';
import { useRefundDetails } from '../hooks/useRefundDetails';
import { IconButton } from './ui/IconButton';
import { InfoCard } from './ui/InfoCard';
import { useTranslation } from 'react-i18next';

type TRefundGiftDrawerProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  type: TGiftOrDonation;
  onPrevOption: () => void;
  onNextOption: () => void;
} & TGetGiftCardsRes['catalog'][0]['brands'][0];

const RefundGiftDrawer = ({
  isOpen,
  setIsOpen,
  brandName,
  description,
  terms,
  disclaimer,
  items,
  type,
  onPrevOption,
  onNextOption,
}: TRefundGiftDrawerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useRefundDetails();

  const formSchema = z.object({
    email: z.string().email(),
    consent: z.boolean(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: data?.refundData.email,
      consent: false,
    },
  });

  const claimTangoMutation = useMutation({
    mutationFn: claimTango,
    onSuccess: () => {
      navigate({
        to: `/payments/thank-you`,
      });
    },
  });

  const onSubmit = async () => {
    try {
      claimTangoMutation.mutate({
        brandName: brandName,
        utid: items[0].utid,
        type,
      });
    } catch (error) {
      console.error(error);
      throw new Error('Failed to claim gift card');
    }
  };

  const iconButtonLabelByType = {
    giftcards: 'Claim Gift Card',
    donations: 'Donate Refund',
    digitalpayments: 'Claim Payment',
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className='flex min-h-[90vh] w-full flex-col gap-5'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex h-[90vh] flex-col items-center justify-between p-5 md:p-10'
          >
            <div className='flex max-h-[80%] min-h-[50%] max-w-[900px] flex-col md:min-h-[90%]'>
              <div className='shrink-0'>
                <DrawerHeader className='p-0'>
                  <div className='mb-5 flex items-center justify-between'>
                    <DrawerTitle className='text-2xl font-semibold'>
                      {brandName}
                    </DrawerTitle>
                    <div className='flex items-center gap-1'>
                      <Button
                        type='button'
                        className='flex size-8 items-center justify-center rounded-md border border-border bg-secondary p-1.5 text-xs text-secondary-foreground outline-none transition-colors duration-200 ease-in-out hover:border-gray-300 hover:bg-primary-700 hover:text-secondary'
                        onClick={onPrevOption}
                      >
                        <ArrowLeft />
                      </Button>
                      <Button
                        type='button'
                        className='flex size-8 items-center justify-center rounded-md border border-border bg-secondary p-1.5 text-xs text-secondary-foreground outline-none transition-colors duration-200 ease-in-out hover:border-gray-300 hover:bg-primary-700 hover:text-secondary'
                        onClick={onNextOption}
                      >
                        <ArrowRight />
                      </Button>
                    </div>
                  </div>
                  <div className='mb-4 flex flex-row items-center justify-between gap-2 rounded-md border border-border bg-primary-600 p-4 lg:flex-col lg:items-start'>
                    <h2 className='text-sm text-card'>{t('Amount')}</h2>
                    <h3 className='flex items-center text-xl font-bold text-card'>
                      $<span>{data?.refundData.refund_amount}</span>
                    </h3>
                  </div>
                </DrawerHeader>

                {type === 'digitalpayments' &&
                  brandName?.toLowerCase() === 'venmo' && (
                    <InfoCard
                      variant='warning'
                      className='mb-4'
                      aria-live='polite'
                    >
                      {t('digitalPayments.venmo.email.help.text')}
                    </InfoCard>
                  )}

                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-bold'>{t('Email')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='johndoe@mail.com'
                          {...field}
                          disabled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <RefundGiftSheetTerms
                description={description}
                terms={terms}
                disclaimer={disclaimer}
              />
            </div>
            <div className='flex flex-col'>
              <div className='flex items-center space-x-2'>
                <FormField
                  control={form.control}
                  name='consent'
                  render={({ field }) => (
                    <FormItem className='flex flex-row items-start space-x-3 space-y-0 p-4'>
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className='space-y-1 leading-none'>
                        <FormLabel className='text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
                          {t('I agree to the Terms of Service')}
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              <div className='flex w-full gap-6'>
                <DrawerClose asChild>
                  <Button variant='outline' type='button' className='w-full'>
                    {t('Cancel')}
                  </Button>
                </DrawerClose>

                <IconButton
                  isLoading={claimTangoMutation.isPending}
                  variant='success'
                  Icon={Check}
                  type='submit'
                  className='w-full'
                  disabled={
                    !form.formState.isValid || !form.getValues('consent')
                  }
                >
                  {t(iconButtonLabelByType[type])}
                </IconButton>
              </div>
            </div>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

export default RefundGiftDrawer;
